// "StreamRift mode": the cockpit for the Rust strategy host on rst. One screen, built for an ultrawide:
// a rail of every instance on the left, the selected instance's quotes, decisions and actions in the middle,
// the lane's telemetry on the right, the account and kill state across the top. Everything polls the relay's
// /api/admin/rust routes; nothing here talks to the engine directly.
//
// Mutations with money behind them (mode → live, kill account, delete) are two-click: the first click arms
// the button, the second fires. There is no browser confirm() anywhere.

import { useCallback, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Plus, Radio, ShieldOff, RotateCcw } from 'lucide-react';
import { useVisibilityAwarePoll } from '@/hooks/useVisibilityAwarePoll';
import { rustLane, fmt, histo, type Decision, type Instance, type Json, type SysHealth } from '@/lib/rustLane/api';
import { Sparkline } from '@/views/G1Health/healthPrimitives';
import { LatencyTable } from '@/views/SysHealth/drilldown';
import { NewStrategy } from './newStrategy';
import { ArmedButton } from './armedButton';
import { QuoteChart } from './quoteChart';
import { Board } from '@/views/WarRoom/board';

const FAST_MS = 1000;
const SLOW_MS = 3000;
const HISTORY = 120;

type Hist = Record<string, number[]>;

function push(h: Hist, k: string, v: number): Hist {
  const arr = [...(h[k] ?? []), v].slice(-HISTORY);
  return { ...h, [k]: arr };
}

export default function StreamRiftView() {
  const [status, setStatus] = useState<Json | null>(null);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Instance | null>(null);
  const [detailAt, setDetailAt] = useState(0);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [health, setHealth] = useState<SysHealth | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [hist, setHist] = useState<Hist>({});
  const lastFlow = useRef<{ at: number; events: number } | null>(null);

  const fast = useCallback(async (signal: AbortSignal) => {
    try {
      const [st, list] = await Promise.all([rustLane.status(signal), rustLane.instances(signal)]);
      if (signal.aborted) return;
      setStatus(st);
      setInstances(list);
      // First instance selected by default; a deleted selection falls back to the first.
      setSelectedId((cur) => (cur && list.some((i) => i.id === cur) ? cur : (list[0]?.id ?? null)));
      setErr(null);
      const lat = (st.latency ?? {}) as Record<string, unknown>;
      const feed = (st.feed ?? {}) as Record<string, unknown>;
      setHist((h) => {
        let n = h;
        for (const k of ['rxToDecide', 'hostLoop', 'feedLag', 'venuePlace', 'rxToAck']) {
          const hh = histo(lat[k]);
          if (hh) n = push(n, `${k}.p99`, hh.p99Us / 1000);
        }
        const ev = typeof feed.flowEvents === 'number' ? feed.flowEvents : null;
        const now = Date.now();
        if (ev !== null) {
          const prev = lastFlow.current;
          if (prev && now > prev.at) n = push(n, 'flow.perS', Math.max(0, (ev - prev.events) / ((now - prev.at) / 1000)));
          lastFlow.current = { at: now, events: ev };
        }
        return n;
      });
    } catch (e) {
      if (signal.aborted) return;
      setErr(e instanceof Error ? e.message : 'failed');
    }
  }, []);
  const { refresh: refreshFast } = useVisibilityAwarePoll(fast, FAST_MS);

  const sel = useCallback(async (signal: AbortSignal) => {
    if (!selectedId) return;
    try {
      const [d, dec] = await Promise.all([rustLane.instance(selectedId, signal), rustLane.decisions(selectedId, 400, signal)]);
      if (signal.aborted) return;
      setDetail(d);
      setDetailAt(Date.now());
      setDecisions(dec);
    } catch (e) {
      if (signal.aborted) return;
      setErr(e instanceof Error ? e.message : 'failed');
    }
  }, [selectedId]);
  const { refresh: refreshSel } = useVisibilityAwarePoll(sel, FAST_MS, { enabled: selectedId !== null });

  const slow = useCallback(async (signal: AbortSignal) => {
    try {
      const h = await rustLane.syshealth(signal);
      if (!signal.aborted) setHealth(h);
    } catch {
      /* the fast poll reports the relay error; telemetry just goes stale */
    }
  }, []);
  useVisibilityAwarePoll(slow, SLOW_MS);

  const act = useCallback(async (label: string, run: () => Promise<unknown>) => {
    try {
      await run();
      setNotice(`${label}: ok`);
      await Promise.all([refreshFast(), refreshSel()]);
    } catch (e) {
      setNotice(`${label}: ${e instanceof Error ? e.message : 'failed'}`);
    }
  }, [refreshFast, refreshSel]);

  const account = (status?.account ?? null) as Json | null;
  const feed = (status?.feed ?? null) as Json | null;
  const risk = (status?.risk ?? null) as Json | null;
  const pnl = (status?.pnl ?? null) as { realizedUsd?: number; unrealizedUsd?: number; netUsd?: number } | null;
  const watching = (status?.watching ?? null) as Json | null;
  const kill = status?.kill ?? null;
  const users = useMemo(() => {
    const u = status?.users;
    if (Array.isArray(u)) return u.map((x) => (typeof x === 'string' ? x : String((x as Json).name ?? ''))).filter(Boolean);
    if (u && typeof u === 'object') return Object.keys(u as Json);
    return Array.from(new Set(instances.map((i) => i.user)));
  }, [status, instances]);

  const groups = useMemo(() => ({
    live: instances.filter((i) => i.armed && i.mode === 'live'),
    shadow: instances.filter((i) => i.armed && i.mode === 'shadow'),
    off: instances.filter((i) => !i.armed),
  }), [instances]);

  const unconfigured = err !== null && /not configured|unconfigured/i.test(err);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* top strip */}
      <div className="border-border flex flex-wrap items-center gap-x-6 gap-y-2 border-b px-4 py-2 font-mono text-xs">
        <div className="flex items-center gap-2"><Radio className="h-4 w-4 text-emerald-400" /><span className="text-sm font-bold">StreamRift mode</span><span className="text-muted-foreground">rst · Rust strategy host</span></div>
        <Chip label="cash" value={fmt.usd(account?.cashUsd)} />
        <Chip label="gate" value={String(account?.gate ?? '—')} tone={account?.gate === 'Open' ? 'ok' : 'warn'} />
        <Chip label="exposure" value={fmt.usd(risk?.accountUsd)} />
        <Chip label="realized" value={fmt.usd(risk?.realizedUsd)} tone={typeof risk?.realizedUsd === 'number' && risk.realizedUsd < 0 ? 'warn' : undefined} />
        <Chip label="rejects" value={fmt.int(risk?.rejectsAccount)} tone={typeof risk?.rejectsAccount === 'number' && risk.rejectsAccount > 0 ? 'warn' : undefined} />
        <Chip label="P&L" value={pnl ? `${fmt.usd(pnl.netUsd)} (${fmt.usd(pnl.realizedUsd)} real · ${fmt.usd(pnl.unrealizedUsd)} open)` : '—'} tone={typeof pnl?.netUsd === 'number' ? (pnl.netUsd >= 0 ? 'ok' : 'warn') : undefined} />
        <Chip label="watching" value={watching ? `${fmt.int(watching.instancesArmed)} inst · ${fmt.int(watching.games)} games · ${fmt.int(watching.books)} books` : '—'} />
        <Chip label="feed" value={feed ? `${feed.connected ? 'up' : 'DOWN'} · ${fmt.int(feed.markets)} mkts · ${fmt.int(feed.flowGaps)} gaps` : '—'} tone={feed?.connected ? 'ok' : 'bad'} />
        <Chip label="instances" value={`${groups.live.length} live · ${groups.shadow.length} shadow · ${groups.off.length} off`} />
        <div className="ml-auto flex items-center gap-2">
          {kill ? (
            <>
              <span className="rounded bg-amber-500/15 px-2 py-1 font-semibold text-amber-400">KILL LATCHED</span>
              <ArmedButton label="reset kill" armedLabel="confirm reset" icon={<RotateCcw className="h-3.5 w-3.5" />} onFire={() => act('kill reset', () => rustLane.killReset('streamrift mode'))} />
            </>
          ) : (
            <>
              <ArmedButton label="kill strategies" armedLabel="confirm: cancel every rp1- order" tone="danger" icon={<ShieldOff className="h-3.5 w-3.5" />} onFire={() => act('kill strategies', () => rustLane.kill('strategies', 'streamrift mode'))} />
              <ArmedButton label="kill account" armedLabel="confirm: cancel EVERY resting order" tone="danger" icon={<ShieldOff className="h-3.5 w-3.5" />} onFire={() => act('kill account', () => rustLane.kill('account', 'streamrift mode'))} />
            </>
          )}
        </div>
      </div>

      {(err || notice) && (
        <div className={`flex items-center gap-2 px-4 py-1.5 text-xs ${err ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> {err ?? notice}
          {unconfigured && <span className="text-muted-foreground">— set G1E_ADMIN_TOKEN in the rst relay env</span>}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 xl:grid-cols-[340px_minmax(0,1fr)_420px]">
        {/* rail */}
        <aside className="border-border flex min-h-0 flex-col border-r">
          <div className="border-border flex items-center justify-between border-b px-3 py-2">
            <span className="text-muted-foreground text-[11px] uppercase tracking-wide">instances</span>
            <button type="button" onClick={() => setShowNew((v) => !v)} className="bg-muted hover:bg-accent flex items-center gap-1 rounded px-2 py-1 text-xs"><Plus className="h-3.5 w-3.5" /> new strategy</button>
          </div>
          {showNew && (
            <div className="border-border border-b p-3">
              <NewStrategy users={users} onCreated={async (inst) => { setShowNew(false); setSelectedId(inst.id); await refreshFast(); }} onNotice={setNotice} />
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {(['live', 'shadow', 'off'] as const).map((g) => (
              <div key={g}>
                <div className="text-muted-foreground bg-muted/40 px-3 py-1 text-[10.5px] uppercase tracking-wide">{g} · {groups[g].length}</div>
                {groups[g].map((i) => (
                  <button key={i.id} type="button" onClick={() => setSelectedId(i.id)} className={`hover:bg-accent block w-full px-3 py-2 text-left ${selectedId === i.id ? 'bg-muted' : ''}`}>
                    <div className="flex items-center justify-between font-mono text-xs">
                      <span className="font-semibold">{i.kind}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] ${i.mode === 'live' ? 'bg-orange-500/15 text-orange-400' : 'bg-sky-500/15 text-sky-400'}`}>{i.mode}</span>
                    </div>
                    <div className="text-muted-foreground mt-0.5 truncate font-mono text-[11px]">{fmt.gameLabel(i.game ?? (i.target?.game as string) ?? (i.target?.event as string))}</div>
                    <div className="text-muted-foreground mt-0.5 flex justify-between font-mono text-[10.5px]"><span>{i.user}</span><span className={typeof (i.pnl as Json | undefined)?.netUsd === 'number' && ((i.pnl as Json).netUsd as number) < 0 ? 'text-amber-400' : ''}>{fmt.usd((i.pnl as Json | undefined)?.netUsd)} · {i.id.slice(0, 8)}</span></div>
                    {(i.blocked || i.error) && <div className="mt-0.5 truncate font-mono text-[10.5px] text-amber-400">{String(i.blocked ?? i.error)}</div>}
                  </button>
                ))}
              </div>
            ))}
            {instances.length === 0 && <p className="text-muted-foreground p-3 text-xs">No instances. Create one to start in shadow.</p>}
          </div>
        </aside>

        {/* center */}
        <main className="min-h-0 overflow-y-auto p-4">
          {detail ? <InstancePanel inst={detail} decisions={decisions} act={act} nowMs={detailAt} /> : <p className="text-muted-foreground text-sm">Select an instance.</p>}
        </main>

        {/* telemetry */}
        <aside className="border-border min-h-0 overflow-y-auto border-l p-3">
          <Telemetry status={status} health={health} hist={hist} />
        </aside>
      </div>
    </div>
  );
}

function Chip({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'warn' | 'bad' }) {
  const c = tone === 'ok' ? 'text-emerald-400' : tone === 'warn' ? 'text-amber-400' : tone === 'bad' ? 'text-red-400' : 'text-foreground';
  return <div><span className="text-muted-foreground">{label} </span><span className={`font-semibold ${c}`}>{value}</span></div>;
}

function InstancePanel({ inst, decisions, act, nowMs }: { inst: Instance; decisions: Decision[]; act: (label: string, run: () => Promise<unknown>) => Promise<void>; nowMs: number }) {
  const [paramsText, setParamsText] = useState<string | null>(null);
  const game = inst.game ?? (inst.target?.game as string | undefined) ?? (inst.target?.event as string | undefined) ?? '—';
  const strategy = (inst.strategy ?? null) as Json | null;
  const legs = Array.isArray(strategy?.legs) ? (strategy!.legs as Json[]) : [];
  const desired = inst.desired ?? [];
  const lastDecisionAt = decisions[0] ? new Date(Number(decisions[0].atMs)).toISOString().slice(11, 19) + 'Z' : null;
  const instPnl = (inst.pnl ?? null) as { realizedUsd?: number; unrealizedUsd?: number; netUsd?: number; fills?: number; filledContracts?: number } | null;
  const positions = (Array.isArray(inst.positions) ? inst.positions : []) as Array<{ ticker: string; side: string; contracts: number; avgCents: number | null; markCents: number | null; costUsd: number; unrealizedUsd: number | null }>;
  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-mono text-lg font-bold">{inst.kind} <span className="text-muted-foreground text-sm font-normal">{inst.id.slice(0, 8)}</span></h2>
          <div className="text-muted-foreground font-mono text-xs">{game} · as {inst.user} · unit {fmt.usd(inst.unitUsd)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 font-mono text-xs">
          <span className={`rounded px-2 py-1 ${inst.armed ? (inst.mode === 'live' ? 'bg-orange-500/15 text-orange-400' : 'bg-sky-500/15 text-sky-400') : 'bg-muted text-muted-foreground'}`}>{inst.armed ? `armed · ${inst.mode}` : 'disarmed'}</span>
          {inst.armed
            ? <ArmedButton label="disarm" armedLabel="confirm disarm (cancels its orders)" onFire={() => act('disarm', () => rustLane.action(inst.id, 'disarm', 'streamrift mode'))} />
            : <button type="button" className="rounded bg-emerald-500/15 px-2 py-1 text-emerald-400 hover:bg-emerald-500/25" onClick={() => act('arm', () => rustLane.action(inst.id, 'arm', 'streamrift mode'))}>arm ({inst.mode})</button>}
          {inst.mode === 'shadow'
            ? <ArmedButton label="mode → live" armedLabel="confirm LIVE: real orders" tone="danger" onFire={() => act('mode live', () => rustLane.mode(inst.id, 'live', 'streamrift mode'))} />
            : <button type="button" className="bg-muted hover:bg-accent rounded px-2 py-1" onClick={() => act('mode shadow', () => rustLane.mode(inst.id, 'shadow', 'streamrift mode'))}>mode → shadow</button>}
          <ArmedButton label="delete" armedLabel="confirm delete" tone="danger" onFire={() => act('delete', () => rustLane.action(inst.id, 'delete', 'streamrift mode'))} />
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 font-mono text-xs md:grid-cols-4 2xl:grid-cols-6">
        <Stat label="exposure" value={fmt.usd(inst.exposureUsd)} />
        <Stat label="live orders" value={fmt.int(inst.live)} />
        <Stat label="blocked" value={inst.blocked ? String(inst.blocked) : 'no'} tone={inst.blocked ? 'warn' : undefined} />
        <Stat label="pulled" value={inst.pulled ? 'yes' : 'no'} tone={inst.pulled ? 'warn' : undefined} />
        <Stat label="last decision" value={lastDecisionAt ?? '—'} />
        <Stat label="decisions (window)" value={fmt.int(decisions.length)} />
        <Stat label="realized" value={fmt.usd(instPnl?.realizedUsd)} tone={typeof instPnl?.realizedUsd === 'number' && instPnl.realizedUsd < 0 ? 'warn' : undefined} />
        <Stat label="unrealized" value={fmt.usd(instPnl?.unrealizedUsd)} tone={typeof instPnl?.unrealizedUsd === 'number' && instPnl.unrealizedUsd < 0 ? 'warn' : undefined} />
        <Stat label="fills" value={`${fmt.int(instPnl?.fills)} · ${fmt.int(instPnl?.filledContracts)} ct`} />
      </div>
      {positions.length > 0 && (
        <section className="border-border rounded-lg border p-3">
          <h3 className="text-muted-foreground mb-2 text-[11px] uppercase tracking-wide">positions on this game (account, marked at the bid)</h3>
          <table className="w-full font-mono text-xs">
            <thead><tr className="text-muted-foreground text-left"><th className="font-normal">ticker</th><th className="font-normal">side</th><th className="text-right font-normal">contracts</th><th className="text-right font-normal">avg</th><th className="text-right font-normal">mark</th><th className="text-right font-normal">cost</th><th className="text-right font-normal">unrealized</th></tr></thead>
            <tbody>{positions.map((p) => (
              <tr key={p.ticker}><td>{p.ticker}</td><td>{p.side}</td><td className="text-right">{fmt.int(p.contracts)}</td><td className="text-right">{p.avgCents?.toFixed(1) ?? '—'}¢</td><td className="text-right">{p.markCents?.toFixed(1) ?? '—'}¢</td><td className="text-right">{fmt.usd(p.costUsd)}</td><td className={`text-right ${typeof p.unrealizedUsd === 'number' && p.unrealizedUsd < 0 ? 'text-amber-400' : ''}`}>{fmt.usd(p.unrealizedUsd)}</td></tr>
            ))}</tbody>
          </table>
        </section>
      )}
      {inst.error && <div className="rounded bg-red-500/10 px-3 py-2 font-mono text-xs text-red-400">{String(inst.error)}</div>}

      {inst.kind === 'scanner' && (
        <section className="border-border rounded-lg border p-3">
          <h3 className="text-muted-foreground mb-2 text-[11px] uppercase tracking-wide">the board (who is on these markets)</h3>
          <Board instance={inst} serverNowMs={nowMs} />
        </section>
      )}
      <section className="border-border rounded-lg border p-3">
        <h3 className="text-muted-foreground mb-2 text-[11px] uppercase tracking-wide">desired quotes</h3>
        {desired.length === 0 ? <p className="text-muted-foreground font-mono text-xs">none (Keep / Pull, or the strategy skipped: {legs.map((l) => String(l.skip ?? '—')).join(' / ') || '—'})</p> : (
          <table className="w-full font-mono text-xs">
            <thead><tr className="text-muted-foreground text-left"><th className="font-normal">slot</th><th className="font-normal">ticker</th><th className="font-normal">side</th><th className="font-normal">action</th><th className="text-right font-normal">price</th><th className="text-right font-normal">contracts</th><th className="font-normal">post-only</th></tr></thead>
            <tbody>{desired.map((q) => (
              <tr key={`${q.slot}-${q.ticker}`}><td>{q.slot}</td><td>{q.ticker}</td><td>{q.side}</td><td>{q.action}</td><td className="text-right">{fmt.cents(q.price)}</td><td className="text-right">{(q.count_h / 100).toFixed(0)}</td><td>{q.postOnly === false ? 'NO' : 'yes'}</td></tr>
            ))}</tbody>
          </table>
        )}
        {desired.length === 2 && <p className="text-muted-foreground mt-2 font-mono text-[11px]">pair sum {fmt.cents(desired[0]!.price + desired[1]!.price)} (box: ≤ 100¢ − edge)</p>}
      </section>

      <section className="border-border rounded-lg border p-3">
        <h3 className="text-muted-foreground mb-2 text-[11px] uppercase tracking-wide">quotes over time</h3>
        <QuoteChart decisions={decisions} />
      </section>

      <div className="grid gap-3 2xl:grid-cols-2">
        <section className="border-border rounded-lg border p-3">
          <h3 className="text-muted-foreground mb-2 text-[11px] uppercase tracking-wide">recent decisions</h3>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full font-mono text-[11px]">
              <thead><tr className="text-muted-foreground text-left"><th className="font-normal">time</th><th className="font-normal">trigger</th><th className="font-normal">quotes</th></tr></thead>
              <tbody>{decisions.slice(0, 60).map((d) => (
                <tr key={d.id}><td className="pr-2 whitespace-nowrap">{new Date(Number(d.atMs)).toISOString().slice(11, 23)}</td><td className="pr-2">{d.trigger}</td><td>{(d.quotes ?? []).map((q) => `${q.ticker.split('-').pop()} ${fmt.cents(q.price)}×${(q.count_h / 100).toFixed(0)}`).join('  ') || 'pull'}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </section>
        <section className="border-border rounded-lg border p-3">
          <div className="mb-2 flex items-center justify-between"><h3 className="text-muted-foreground text-[11px] uppercase tracking-wide">params</h3>
            {paramsText === null
              ? <button type="button" className="bg-muted hover:bg-accent rounded px-2 py-0.5 font-mono text-[11px]" onClick={() => setParamsText(JSON.stringify(inst.params ?? strategy?.params ?? {}, null, 2))}>edit</button>
              : <div className="flex gap-1"><button type="button" className="rounded bg-emerald-500/15 px-2 py-0.5 font-mono text-[11px] text-emerald-400" onClick={() => { try { const p = JSON.parse(paramsText) as Json; void act('params', () => rustLane.params(inst.id, p, 'streamrift mode')).then(() => setParamsText(null)); } catch { void act('params', () => Promise.reject(new Error('params is not valid JSON'))); } }}>save</button><button type="button" className="bg-muted rounded px-2 py-0.5 font-mono text-[11px]" onClick={() => setParamsText(null)}>cancel</button></div>}
          </div>
          {paramsText === null
            ? <pre className="bg-muted max-h-72 overflow-auto rounded p-2 font-mono text-[11px]">{JSON.stringify(inst.params ?? strategy?.params ?? {}, null, 2)}</pre>
            : <textarea id={`params-${inst.id}`} value={paramsText} onChange={(e) => setParamsText(e.target.value)} className="bg-muted h-72 w-full rounded p-2 font-mono text-[11px]" spellCheck={false} />}
        </section>
      </div>

      <details className="text-xs"><summary className="text-muted-foreground cursor-pointer">raw instance</summary><pre className="bg-muted mt-2 max-h-96 overflow-auto rounded p-2 font-mono text-[10.5px]">{JSON.stringify(inst, null, 1)}</pre></details>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div className="border-border rounded border px-2.5 py-1.5">
      <div className="text-muted-foreground text-[10px] uppercase tracking-wide">{label}</div>
      <div className={`mt-0.5 truncate text-sm font-semibold ${tone === 'warn' ? 'text-amber-400' : ''}`}>{value}</div>
    </div>
  );
}

function Telemetry({ status, health, hist }: { status: Json | null; health: SysHealth | null; hist: Hist }) {
  const lat = (status?.latency ?? null) as Record<string, unknown> | null;
  const feed = (status?.feed ?? null) as Json | null;
  return (
    <div className="space-y-4 font-mono text-xs">
      <section>
        <h3 className="text-muted-foreground mb-1 text-[11px] uppercase tracking-wide">engine latency (p99, ms)</h3>
        {(['rxToDecide', 'hostLoop', 'feedLag', 'venuePlace', 'rxToAck'] as const).map((k) => {
          const h = histo(lat?.[k]);
          const series = hist[`${k}.p99`] ?? [];
          return (
            <div key={k} className="mb-2">
              <div className="flex justify-between"><span>{k}</span><span className="text-muted-foreground">{h && h.n ? `${fmt.us(h.p50Us)} / ${fmt.us(h.p99Us)}` : 'no samples'}</span></div>
              {series.length > 1 && <Sparkline values={series} color={k === 'feedLag' ? '#4fa3ff' : '#ff9f43'} height={28} />}
            </div>
          );
        })}
      </section>
      <section>
        <h3 className="text-muted-foreground mb-1 text-[11px] uppercase tracking-wide">flow</h3>
        <div className="flex justify-between"><span>events / s</span><span className="text-muted-foreground">{hist['flow.perS']?.length ? (hist['flow.perS']![hist['flow.perS']!.length - 1] ?? 0).toFixed(0) : '—'}</span></div>
        {(hist['flow.perS']?.length ?? 0) > 1 && <Sparkline values={hist['flow.perS']!} color="#3ddc97" height={28} />}
        <div className="text-muted-foreground mt-1 flex justify-between"><span>gaps</span><span>{fmt.int(feed?.flowGaps)} · session {fmt.int(feed?.session)} · offset {fmt.int(feed?.clockOffsetMs)} ms</span></div>
      </section>
      <section>
        <h3 className="text-muted-foreground mb-1 text-[11px] uppercase tracking-wide">rust hydras</h3>
        {health ? health.rsh.map((b) => (
          <div key={b.id} className="mb-1 flex items-center justify-between">
            <span><span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${b.ok && b.summary?.ready ? 'bg-emerald-400' : b.ok ? 'bg-amber-400' : 'bg-red-400'}`} />{b.id}{health.feedgw.summary?.publisher === b.id ? ' ·primary' : ''}</span>
            <span className="text-muted-foreground">{b.summary ? `${fmt.int(b.summary.markets)} · ${b.summary.consumers}c` : b.error}</span>
          </div>
        )) : <p className="text-muted-foreground">loading…</p>}
        {health?.feedgw.summary && <div className="text-muted-foreground mt-1">feedgw: {fmt.int(health.feedgw.summary.tickers)} tickers on floor · {health.feedgw.summary.clients} sockets · fallback {health.feedgw.summary.fallbackLabels.join(',') || 'none'}</div>}
      </section>
      <section>
        <h3 className="text-muted-foreground mb-1 text-[11px] uppercase tracking-wide">all stages</h3>
        <LatencyTable latency={lat} />
      </section>
    </div>
  );
}
